/**
 * Configuracion en tiempo de ejecucion.  DF-11.
 *
 * NO se usa `import.meta.env`: un empaquetador de navegador incrusta esas
 * variables en el artefacto durante la construccion, y entonces cada entorno
 * exige su propia construccion. Lo que se prueba en `testing` dejaria de ser
 * el binario que llega a `production` (Art. X).
 *
 * En su lugar, /config.json se sirve JUNTO al artefacto y se lee al arrancar.
 * Un solo artefacto recorre los tres entornos.
 *
 * F-IX.3: ese archivo es publico. Ahi no va ningun secreto.
 */

export interface RuntimeConfig {
  /**
   * ORIGEN del backend, SIN ruta: `https://api.ejemplo.com`.
   *
   * No lleva `/api/v1` porque el contrato ya declara las rutas completas. Se
   * llamaba `apiBaseUrl` hasta el 25-08-2026, y ese nombre invitaba a incluir
   * la base: el resultado era `/api/v1/api/v1/auth/login`, que no falla al
   * compilar y solo se ve en la primera llamada real.
   */
  readonly apiOrigin: string;
  readonly environment: 'development' | 'testing' | 'production';
  /**
   * Las credenciales con las que el formulario de acceso **arranca relleno**,
   * **solo en `development`**. Encargo del responsable del proyecto del
   * 15-09-2026: entrar veinte veces al dia escribiendo lo mismo es lo que hace
   * que nadie pruebe el acceso.
   *
   * ── Lo que esto NO es, y la regla que sigue en pie ───────────────────────
   *
   * `F-IX.3`: este archivo es publico y **ahi no va ningun secreto**. Esto no
   * la contradice porque lo que va aqui no es un secreto del sistema: es la
   * cuenta de pruebas de un backend local, en un archivo que **no esta en el
   * repositorio** (`public/config.json` esta ignorado; el ejemplo no lo trae).
   * Y para que no pueda convertirse en uno, **fuera de `development` se
   * descarta al leer**, con aviso en consola: un `config.json` de `testing` o
   * `production` que lo traiga por descuido no rellena nada.
   *
   * El codigo de 2FA no va: cambia cada treinta segundos y escribirlo es lo
   * unico que de verdad tiene que hacer quien entra.
   */
  readonly devLogin?: DevLogin;
  /**
   * La **clave publicable** de Stripe (`pk_test_…` o `pk_live_…`), con la que
   * la app pinta el formulario de tarjeta (`RF-FE-108`, 02-10-2026).
   *
   * Es publica por diseño de Stripe —va en el navegador de todos—, de modo que
   * `F-IX.3` se cumple: la secreta vive solo en el backend. **Opcional**: sin
   * ella no se pide la tarjeta y comprar con tarjeta queda pendiente, como antes
   * de Stripe. Ver `getStripeKey()`.
   */
  readonly stripePublishableKey?: string;
  /**
   * La marca blanca de esta instancia (`RF-FE-161`). La escribe el script de
   * arranque desde `BRAND_NAME`, `BRAND_ACCENT` y `/branding/logo.svg`, y solo
   * si hay marca: sin ella la clave no existe y todo es NEXUS. Ver `getBrand()`.
   */
  readonly brand?: BrandConfig;
}

export interface BrandConfig {
  readonly name?: string;
  readonly accent?: string;
  /** Hay `/branding/logo.svg`. La ruta es fija para no abrir la CSP (plan `A-2`). */
  readonly logo?: boolean;
  /** `#rrggbb` en minusculas (`RF-FE-162`). Ver `colorDeMarca.ts`. */
  readonly color?: string;
}

/** La marca resuelta. `propia` decide si se pinta el logotipo NX (spec 161 §5). */
export interface Brand {
  readonly name: string;
  readonly accent: string;
  /** El sistema: «NEXUS paga». En una marca blanca, su nombre (spec 161 §7.1). */
  readonly system: string;
  readonly logo: boolean;
  readonly propia: boolean;
  /** El color principal de la marca, o `null`: el violeta de NEXUS (`RF-FE-162`). */
  readonly color: string | null;
}

const NEXUS: Brand = { name: 'Nexus', accent: 'Trading', system: 'NEXUS', logo: false, propia: false, color: null };

export interface DevLogin {
  readonly identifier: string;
  readonly password: string;
}

let config: RuntimeConfig | null = null;

/** Se invoca una vez, antes de montar la aplicacion. */
export async function loadConfig(): Promise<RuntimeConfig> {
  const response = await fetch('/config.json', { cache: 'no-store' });

  if (!response.ok) {
    // Art. IX.5: fallar de forma explicita, nunca asumir un valor por defecto.
    throw new Error(
      `No se pudo cargar /config.json (HTTP ${String(response.status)}). La aplicacion no puede arrancar.`,
    );
  }

  const parsed: unknown = await response.json();
  config = assertRuntimeConfig(parsed);
  return config;
}

export function getConfig(): RuntimeConfig {
  if (config === null) {
    throw new Error('getConfig() se invoco antes de loadConfig().');
  }
  return config;
}

/**
 * Las credenciales de desarrollo, o `null`. Ver `RuntimeConfig.devLogin`.
 *
 * `null` tambien antes de `loadConfig()`: el formulario de acceso se monta en
 * pruebas sin configuracion, y ahi la respuesta correcta es «no hay», no una
 * excepcion.
 */
export function getDevLogin(): DevLogin | null {
  return config?.devLogin ?? null;
}

/**
 * La clave publicable de Stripe, o `null` si no hay o no lo parece.
 *
 * Una que no empiece por `pk_` se descarta **con aviso**: una `sk_` aqui seria
 * la secreta publicada, y conviene que alguien lo vea. `null` tambien antes de
 * `loadConfig()`, como `getDevLogin()`.
 */
export function getStripeKey(): string | null {
  const clave = config?.stripePublishableKey;
  if (typeof clave !== 'string' || clave.trim() === '') return null;
  if (!clave.startsWith('pk_')) {
    console.warn('/config.json trae una stripePublishableKey que no empieza por pk_: se ignora.');
    return null;
  }
  return clave;
}

/**
 * La marca de esta instancia, o NEXUS. Como `getDevLogin()`, responde tambien
 * antes de `loadConfig()`: las pruebas montan textos sin configuracion, y ahi
 * la respuesta correcta es NEXUS, no una excepcion.
 */
export function getBrand(): Brand {
  const brand = config?.brand;
  if (brand === undefined) return NEXUS;

  const name = brand.name ?? NEXUS.name;
  return {
    name,
    accent: brand.accent ?? (brand.name === undefined ? NEXUS.accent : ''),
    system: brand.name ?? NEXUS.system,
    logo: brand.logo === true,
    propia: true,
    color: brand.color ?? null,
  };
}

/** Plan 161 `A-4`: lo que no tenga forma se descarta con aviso, sin tumbar la app. */
function saneaBrand(valor: unknown): BrandConfig | null {
  if (valor === undefined) return null;
  const texto = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined);
  const crudo = typeof valor === 'object' && valor !== null ? (valor as Record<string, unknown>) : {};
  const name = texto(crudo.name);
  const accent = texto(crudo.accent);
  // Plan 162 `A-2`: el script ya valido; uno escrito a mano y mal se ignora con aviso.
  const color =
    typeof crudo.color === 'string' && /^#[0-9a-f]{6}$/i.test(crudo.color) ? crudo.color.toLowerCase() : undefined;
  if (crudo.color !== undefined && color === undefined) {
    console.warn('/config.json trae un brand.color que no es #rrggbb: se ignora.');
  }
  const brand: BrandConfig = {
    ...(name !== undefined && { name }),
    ...(accent !== undefined && { accent }),
    ...(crudo.logo === true && { logo: true }),
    ...(color !== undefined && { color }),
  };

  if (Object.keys(brand).length === 0) {
    console.warn('/config.json trae un brand sin nombre ni logo: se ignora.');
    return null;
  }
  return brand;
}

function assertRuntimeConfig(value: unknown): RuntimeConfig {
  if (typeof value !== 'object' || value === null) {
    throw new Error('/config.json no contiene un objeto.');
  }

  const candidate = value as Record<string, unknown>;
  const missing = (['apiOrigin', 'environment'] as const).filter(
    (key) => typeof candidate[key] !== 'string' || candidate[key] === '',
  );

  if (missing.length > 0) {
    throw new Error(`/config.json no declara: ${missing.join(', ')}.`);
  }

  const { devLogin, brand, ...resto } = candidate;
  const marca = saneaBrand(brand);
  const base = { ...resto, ...(marca && { brand: marca }) } as unknown as RuntimeConfig;
  const credenciales = saneaDevLogin(devLogin);

  if (credenciales === null) return base;

  /*
   * Fuera de desarrollo **no se rellena nada**, venga lo que venga: es lo que
   * impide que una comodidad de desarrollo llegue a un entorno donde seria una
   * credencial publicada. Se avisa, porque un `config.json` que lo traiga ahi
   * es un descuido que alguien tiene que ver.
   */
  if (base.environment !== 'development') {
    console.warn(
      `/config.json trae devLogin en ${base.environment}, y solo se admite en development: se ignora.`,
    );
    return base;
  }

  return { ...base, devLogin: credenciales };
}

/** Los dos textos, no vacios; cualquier otra forma es «no hay». */
function saneaDevLogin(valor: unknown): DevLogin | null {
  if (typeof valor !== 'object' || valor === null) return null;

  const { identifier, password } = valor as Record<string, unknown>;
  if (typeof identifier !== 'string' || identifier === '') return null;
  if (typeof password !== 'string' || password === '') return null;

  return { identifier, password };
}
